/**
 * useProjectData — the single fetch/hydrate implementation for a project's
 * data (project doc, active OST tree, and shared insight data).
 *
 * Used by ProjectDataLoader to gate the /app/project/:projectId/* route
 * subtree. Any page nested under that route reads already-hydrated data
 * from the store instead of fetching it itself.
 */
import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store/useStore';
import { getProject } from '../api/projects';
import { wakeBackendIfNeeded } from '../api/heartbeat';
import { isErrorResponse } from '../utils/apiUtils';
import { createEmptyOst } from '../utils/ostUtils';
import { OstState, ProjectModel } from '../types';

interface UseProjectDataResult {
    loading: boolean;
    error: string | null;
}

export function useProjectData(projectId: string | undefined): UseProjectDataResult {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Blocks the OST auto-persist effect from firing while a load is in
    // progress. Kept as a ref (not state) so it can be read synchronously.
    const isLoadingRef = useRef(false);

    const {
        user,
        setOst,
        setActiveOst,
        setActiveProject,
        setProjectSharedData,
        setPlanBoardData,
        setOstLoadedForProjectId,
        clearProject,
    } = useStore();

    useEffect(() => {
        if (!projectId || !user) return;

        let cancelled = false;

        const state = useStore.getState();
        const currentActive = state.activeProject;
        const alreadyLoaded = !!(
            currentActive &&
            currentActive._id === projectId &&
            state.ostLoadedForProjectId === projectId
        );
        if (alreadyLoaded) {
            setLoading(false);
            setError(null);
            isLoadingRef.current = false;
            return;
        }

        const load = async () => {
            setLoading(true);
            setError(null);
            isLoadingRef.current = true;
            setOst(createEmptyOst());

            // Fire-and-forget: wake Render-hosted Python AI backend if needed.
            wakeBackendIfNeeded().catch((e) => console.debug('Heartbeat ping failed', e));

            const result = await getProject(projectId);
            if (cancelled) return;

            if (isErrorResponse(result)) {
                setError(result.error);
                isLoadingRef.current = false;
                setLoading(false);
                return;
            }

            type ProjectWithOst = ProjectModel & { ost?: OstState; osts?: any[]; insights?: any[] };
            const project = result as ProjectWithOst;

            setActiveProject(project as ProjectModel);
            setPlanBoardData({
                placements: project.planBoard?.placements || {},
                order: project.planBoard?.order || [],
                lines: project.planBoard?.lines || [],
                axisLabels: project.planBoard?.axisLabels || [],
                zoom: project.planBoard?.zoom,
                connections: project.planBoard?.connections || [],
                enablerCards: project.planBoard?.enablerCards || [],
            });

            if (project.osts && project.osts.length > 0) {
                const chosen = project.osts[0];
                const ostPayload = chosen.ost || {};
                const sharedInsights = ostPayload.insights?.length
                    ? ostPayload.insights
                    : (project.insights || []);

                setActiveOst(String(chosen._id));
                setOst(ostPayload as OstState);
                setProjectSharedData({
                    insights: sharedInsights,
                    insightLinks: ostPayload.insightLinks || [],
                    triageItems: ostPayload.triageItems || [],
                });
            } else {
                const ostPayload: OstState = project.ost || createEmptyOst();
                const sharedInsights = ostPayload.insights?.length
                    ? ostPayload.insights
                    : (project.insights || []);

                setActiveOst(null);
                setOst(ostPayload as OstState);
                setProjectSharedData({
                    insights: sharedInsights,
                    insightLinks: ostPayload.insightLinks || [],
                    triageItems: ostPayload.triageItems || [],
                });
            }

            // Mark the project as fully hydrated so a later remount (e.g.
            // navigating away and back) can skip re-fetching.
            setOstLoadedForProjectId(projectId);

            // Small delay so the OST auto-persist debounce timer (600 ms) in
            // ProjectPage doesn't fire and overwrite fresh data with the
            // cleared state set above.
            setTimeout(() => {
                if (!cancelled) {
                    isLoadingRef.current = false;
                    setLoading(false);
                }
            }, 700);
        };

        load();

        return () => {
            cancelled = true;
            clearProject();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, user]);

    return { loading, error };
}
